<template>

    <div class="widget-content-text">

        <dmb-widget-styled-text v-if="widget.settings" :style="widget.settings.textStyle">{{ renderedContent }}</dmb-widget-styled-text>

    </div>

</template>

<script>

import { Vue, Options } from 'vue-class-component'
import { DateTime } from 'luxon'

import WidgetStyledText from "../WidgetStyledText.vue";

// Shortcode token for a live clock: {time} or {time:HH:mm:ss}. Group 1 = optional luxon format.
const TIME_TOKEN = /\{time(?::([^}]+))?\}/gi;
const DEFAULT_TIME_FORMAT = 'HH:mm';

export default Options({
    name: 'dmb-widget-content-text',
    components: {
        [WidgetStyledText.name]: WidgetStyledText
    },
    props: {
        widget: Object,
        freeze: Boolean
    }
})(
    class WidgetContentText extends Vue {

        now = Date.now();
        clockTimer = null;

        get renderedContent() {
            const content = this.widget?.settings?.content ?? '';
            if (!this.hasToken(content)) {
                return content; // plain text: zero formatting cost, no timer
            }

            const dt = DateTime.fromMillis(this.now); // referencing this.now re-runs this getter on each tick
            TIME_TOKEN.lastIndex = 0;
            return content.replace(TIME_TOKEN, (full, format) => {
                try {
                    return dt.toFormat(format || DEFAULT_TIME_FORMAT);
                } catch (e) {
                    return full; // malformed format string: leave the token literal
                }
            });
        }

        created() {
            // Runs immediately and re-arms whenever the content changes (token added/removed, seconds toggled).
            this.$watch(() => this.widget?.settings?.content, this.startClockIfNeeded, { immediate: true });
        }

        beforeUnmount() {
            this.stopClock();
        }

        startClockIfNeeded() {
            this.stopClock();

            const content = this.widget?.settings?.content;
            if (!content || !this.hasToken(content)) {
                return;
            }

            // Tick every second only when seconds are shown, otherwise every 10s.
            const interval = this.showsSeconds(content) ? 1000 : 10000;
            this.now = Date.now();
            this.clockTimer = window.setInterval(() => { this.now = Date.now(); }, interval);
        }

        stopClock() {
            if (this.clockTimer) {
                window.clearInterval(this.clockTimer);
                this.clockTimer = null;
            }
        }

        hasToken(content) {
            TIME_TOKEN.lastIndex = 0;
            return TIME_TOKEN.test(content);
        }

        showsSeconds(content) {
            TIME_TOKEN.lastIndex = 0;
            let m;
            while ((m = TIME_TOKEN.exec(content)) !== null) {
                if (m[1] && m[1].includes('s')) {
                    return true;
                }
            }
            return false;
        }
    }
)

</script>
<style src="../../../scss/component/widget/WidgetContentText.scss" lang="scss"></style>
